import React, {FormEvent, ReactElement, useEffect, useRef, useState} from 'react';
import cn from 'classnames';

import './styles.scss';

const ACCESS_GATE_ENABLED = false;

const STORAGE_KEY = 'mira-now-access-granted';
const ACCESS_CODE = 'scp2026!';

const REAL_TITLE = 'Mira Now';
const REAL_DESCRIPTION = 'Mira Now';
const REAL_FAVICON = '/favicon.ico';

type Props = {
  children: ReactElement;
};

export const AccessGate = ({children}: Props): JSX.Element => {
  const [granted, setGranted] = useState(() => !ACCESS_GATE_ENABLED || localStorage.getItem(STORAGE_KEY) === 'true');
  const [expanded, setExpanded] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  useEffect(() => {
    document.body.classList.toggle('access-gate-locked', !granted);

    if (granted) {
      document.title = REAL_TITLE;

      const descriptionTag = document.querySelector('meta[name="description"]');

      descriptionTag?.setAttribute('content', REAL_DESCRIPTION);

      const iconTag = document.querySelector('link[rel="icon"]');

      iconTag?.setAttribute('href', REAL_FAVICON);
    }

    return () => document.body.classList.remove('access-gate-locked');
  }, [granted]);

  function handleOpen() {
    setExpanded(true);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);

    if (error) {
      setError(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (value === ACCESS_CODE) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setGranted(true);

      return;
    }

    setError(true);
    setValue('');
    inputRef.current?.focus();
  }

  if (granted) {
    return children;
  }

  return (
    <div className="access-gate">
      <div className="access-gate__content">
        {!expanded && (
          <button
            type="button"
            className="access-gate__button"
            onClick={handleOpen}
          >
            Enter code
          </button>
        )}

        <div className={cn('access-gate__reveal', {'access-gate__reveal--open': expanded})}>
          <form className="access-gate__form" onSubmit={handleSubmit}>
            <label className="access-gate__label" htmlFor="access-gate-code">
              Access code
            </label>
            <div className="access-gate__field-row">
              <input
                ref={inputRef}
                id="access-gate-code"
                className={cn('access-gate__input', {'access-gate__input--error': error})}
                type="password"
                autoComplete="off"
                value={value}
                onChange={handleChange}
                aria-invalid={error}
                aria-describedby={error ? 'access-gate-error' : undefined}
                tabIndex={expanded ? 0 : -1}
              />
              <button type="submit" className="access-gate__submit" aria-label="Unlock">
                &rarr;
              </button>
            </div>
            {error && (
              <span className="access-gate__error" id="access-gate-error" role="alert">
                Incorrect code, try again.
              </span>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};
